<template>

    <AppHead title="Team" />


    <section class="px-4 xl:px-0 pb-16 pt-6 md:pt-0">
        <SectionHeading title="Team">
            <template v-slot="right">
                <div @click="open = true">
                    <Button label="Invite people" icon="UserAddIcon" />
                </div>
            </template>
        </SectionHeading>

        <div class="flex flex-col">

            <div class="bg-white rounded-xl md:hidden">
                <div class="grid divide-y divide-gray-400">
                    <Link :href="route('users.show', user)" v-for="(user, userIndex) in users.data" :key="user.uuid" class="w-full flex flex-wrap space-x-3 p-3.5">
                        <div class="">
                            <div v-if="user.avatar" class="shrink-0 h-20 w-20 bg-cover bg-center rounded-lg bg-gray-300" :style="'background-image: url(' + user.avatar + ')'" />
                            <div class="flex items-center justify-center shrink-0 h-20 w-20 bg-cover bg-center rounded-lg bg-gray-300 font-medium text-gray-700 text-sm" v-if="!user.avatar">
                                {{ user.name.charAt(0) }}
                            </div>
                        </div>
                        <div class="text-sm font-medium flex flex-col justify-between flex-1 py-0.5">
                            <div class="w-full">
                                <div class="flex space-x-1.5 justify-between mb-0.5">
                                    <div class="flex-1 leading-tight line-clamp-2">
                                        <div class="font-semibold">
                                            {{ user.name }}
                                        </div>
                                        <div class="text-gray-700">
                                            {{ user.email }}
                                        </div>
                                    </div>
                                    <div class="">
                                        <div class="px-2 py-1 flex justify-start items-start text-xs font-semibold rounded-full bg-gray-400 shrink-0">
                                            {{ users.data[userIndex].selectedRole }}
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div class="flex w-full justify-between items-center text-gray-700">
                                <div class="flex space-x-1 items-center">
                                    <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                    <span>{{ user.timezone }}</span>
                                </div>
                            </div>
                        </div>
                    </Link>
                    <div v-if="users.links.length > 3">
                        <div class="w-full flex justify-between items-center p-3 space-x-2 text-sm font-medium">
                            <div class="text-gray-800 font-normal text-xs line-clamp-1">
                                Showing <span class="font-bold">{{ users.from }}</span> to <span class="font-bold">{{ users.to }}</span> of <span class="font-bold">{{ users.total }}</span> users
                            </div>
                            <div class="">
                                <div class="grid grid-flow-col divide-x divide-gray-400 border border-gray-400 rounded">
                                    <div v-for="(link, index) in users.links">
                                        <Link v-if="index === 0 && users.prev_page_url" class="px-2 h-10 text-gray-800 flex items-center justify-center rounded-l  hover:bg-gray-400" :href="users.prev_page_url" :only="['users']" preserve-scroll>
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                                        </Link>
                                        <div v-if="index === 0 && !users.prev_page_url" class="text-gray-500 cursor-no-drop px-2 h-10 flex items-center justify-center rounded-l ">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                                        </div>
                                        <Link v-if="index !== 0 && index !== users.links.length - 1" class="px-4 h-10 flex items-center justify-center" :href="link.url" :only="['users']" preserve-scroll :class="{ 'bg-regal-blue-100 text-regal-blue-700': link.active, 'text-gray-800 hover:bg-gray-300': !link.active}">
                                            {{ link.label }}
                                        </Link>
                                        <Link v-if="index === users.links.length - 1 && users.next_page_url" class="px-2 h-10 text-gray-800 flex items-center justify-center rounded-r  hover:bg-gray-400" :href="users.next_page_url" :only="['users']" preserve-scroll>
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                                        </Link>
                                        <div v-if="index === users.links.length - 1 && !users.next_page_url" class="text-gray-500 cursor-no-drop px-2 h-10 flex items-center justify-center rounded-r " :href="users.next_page_url" :only="['users']" preserve-scroll>
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 hidden md:block">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="overflow-hidden sm:rounded-2xl">
                        <table class="min-w-full divide-y divide-gray-300 w-full table-fixed">
                            <thead class="bg-white">
                                <tr>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Title
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Timezone
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">

                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-300">
                                <tr v-for="(user, userIndex) in users.data" :key="user.uuid">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-300 bg-center bg-cover border border-gray-400" :style="'background-image: url(' + user.avatar + ')'">
                                                <div class="font-medium text-gray-700 text-sm" v-if="!user.avatar">
                                                    {{ user.name.charAt(0) }}
                                                </div>
                                            </div>
                                            <div class="ml-4">
                                                <Link :href="route('users.show', user)" class="text-sm leading-tight font-medium text-gray-900 hover:underline flex space-x-1 items-center cursor-pointer">
                                                    {{ user.name }}
                                                </Link>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm font-medium text-gray-700"> {{ user.title }} </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                        {{ user.email }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                                        <select :disabled="disableRoleSelect" @change="updateRole(user)" class="inline-flex appearance-none bg-white border border-transparent hover:bg-gray-400 px-4 py-2 pr-8 rounded-full text-sm -ml-2 leading-5 font-semibold rounded-full bg-gray-300 cursor-pointer focus:outline-none focus:shadow-outline" name="" v-model="users.data[userIndex].selectedRole" v-if="users.data[userIndex].selectedRole != 'Super Admin' && users.data[userIndex].selectedRole != 'Owner'">
                                            <option v-for="(role, roleIndex) in user.roles" :disabled="role.name == 'invited'">{{ role.name }}
                                            </option>
                                        </select>
                                        <div class="" v-else>
                                            {{ users.data[userIndex].selectedRole }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                        {{ user.timezone }}
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div @click="deleteUser(user)" v-if="users.data[userIndex].selectedRole != 'Super Admin' && users.data[userIndex].selectedRole != 'Owner'" class="flex space-x-1.5 items-center text-gray-800 hover:text-red-500 cursor-pointer">
                                            <div class="">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                            </div>
                                            <div class="">
                                                Delete
                                            </div>
                                        </div>
                                        <div v-else class="flex items-center space-x-1.5 text-gray-700 hover:text-regal-blue-700">
                                            <div class="">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path></svg>
                                            </div>
                                            <a class="" href="mailto:hello@pager.fm">Transfer ownership</a>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="users.links.length > 3">
                                    <td colspan="6">
                                        <div class="w-full flex justify-between items-center px-6 py-3 pr-8 text-sm font-medium">
                                            <div class="text-gray-800 font-normal">
                                                Showing <span class="font-bold">{{ users.from }}</span> to <span class="font-bold">{{ users.to }}</span> of <span class="font-bold">{{ users.total }}</span> users
                                            </div>
                                            <div class="">
                                                <div class="grid grid-flow-col divide-x divide-gray-400 border border-gray-400 rounded">
                                                    <div v-for="(link, index) in users.links">
                                                        <Link v-if="index === 0 && users.prev_page_url" class="px-2 h-10 text-gray-800 flex items-center justify-center rounded-l  hover:bg-gray-400" :href="users.prev_page_url" :only="['users']" preserve-scroll>
                                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                                                        </Link>
                                                        <div v-if="index === 0 && !users.prev_page_url" class="text-gray-500 cursor-no-drop px-2 h-10 flex items-center justify-center rounded-l ">
                                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                                                        </div>
                                                        <Link v-if="index !== 0 && index !== users.links.length - 1" class="px-4 h-10 flex items-center justify-center" :href="link.url" :only="['users']" preserve-scroll :class="{ 'bg-regal-blue-100 text-regal-blue-700': link.active, 'text-gray-800 hover:bg-gray-300': !link.active}">
                                                            {{ link.label }}
                                                        </Link>
                                                        <Link v-if="index === users.links.length - 1 && users.next_page_url" class="px-2 h-10 text-gray-800 flex items-center justify-center rounded-r  hover:bg-gray-400" :href="users.next_page_url" :only="['users']" preserve-scroll>
                                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                                                        </Link>
                                                        <div v-if="index === users.links.length - 1 && !users.next_page_url" class="text-gray-500 cursor-no-drop px-2 h-10 flex items-center justify-center rounded-r " :href="users.next_page_url" :only="['users']" preserve-scroll>
                                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="px-4 xl:px-0 pb-24">
        <SectionHeading :title="invites.count" importance="2"/>

        <div class="flex flex-col">

            <div class="bg-white rounded-xl md:hidden">
                <div class="grid divide-y divide-gray-400">
                    <div v-for="invitee in invites.invites" :key="invitee.id" class="w-full flex flex-wrap space-x-3 p-3.5">
                        <div class="">
                            <div class="flex items-center justify-center shrink-0 h-20 w-20 bg-cover bg-center rounded-lg bg-gray-300 font-medium text-gray-700 text-sm">
                                {{ invitee.email.charAt(0) }}
                            </div>
                        </div>
                        <div class="text-sm font-medium flex flex-col justify-between flex-1 py-0.5">
                            <div class="w-full">
                                <div class="flex space-x-1.5 justify-between mb-0.5">
                                    <div class="flex-1 leading-tight line-clamp-2">
                                        <div class="font-semibold">
                                            {{ invitee.email }}
                                        </div>
                                        <div class="text-gray-700">
                                            Invited by {{ invitee.inviter.name }}
                                        </div>
                                    </div>
                                    <div class="">
                                        <div class="px-2 py-1 flex justify-start items-start text-xs font-semibold rounded-full bg-gray-400 shrink-0">
                                            {{ invitee.status }}
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div class="flex w-full justify-between items-center text-gray-700">
                                <div class="flex space-x-1 items-center">
                                    <span>{{ invitee.invited }}</span>
                                </div>
                                <div class="">
                                    <div @click="deleteInvitation(invitee)" class="flex space-x-1.5 items-center text-gray-800 hover:text-red-500 cursor-pointer">
                                        <div class="">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                        </div>
                                        <div class="">
                                            Delete
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 md:block hidden">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="overflow-hidden sm:rounded-2xl">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-white">
                                <tr>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Invited by
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" class="px-6 pt-6 pb-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">

                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-300">
                                <tr v-for="invitee in invites.invites" :key="invitee.id">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-300 bg-center bg-cover border border-gray-400">
                                                <div class="font-medium text-gray-700 text-sm">
                                                    {{ invitee.email.charAt(0).toUpperCase() }}
                                                </div>
                                            </div>
                                            <div class="ml-4 text-sm font-medium text-gray-700">
                                                {{ invitee.email }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm font-medium text-gray-700 flex space-x-1">
                                            <Link :href="route('users.show', invitee.inviter)" class="hover:underline text-gray-800">
                                                {{ invitee.inviter.name }}
                                            </Link>
                                            <div class="">
                                                ({{ invitee.inviter.email }})
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm font-medium text-gray-700"> {{ invitee.invited }} </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                        <div class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-400">
                                            {{ invitee.status }}
                                        </div>
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div @click="deleteInvitation(invitee)" class="flex space-x-1.5 items-center text-gray-800 hover:text-red-500 cursor-pointer">
                                            <div class="">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                            </div>
                                            <div class="">
                                                Delete
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="!invites.invites.length" class="bg-white w-full flex items-center justify-center pt-4">
                            <div class="w-auto py-10 md:py-0 md:h-64 flex flex-col space-y-5 items-center justify-center">
                                <div class="flex flex-col items-center">
                                    <svg class="w-8 h-8 mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                    <div class="text-sm font-medium">
                                        No invites, yet.
                                    </div>
                                    <div class="text-gray-700 text-sm text-center">
                                        Let‘s get some more people on your team.
                                    </div>
                                </div>
                                <div @click="open = true">
                                    <Button label="Invite people" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <Modal v-model:open="open" @close="deleteQueryParam">
        <div class="w-full">
            <component :is="component" :sharedInvite="sharedInvite" @editLink="switchComponents" :expirationOptions="expirationOptions" v-model:open="open" @update:open="resetModal"/>
        </div>
    </Modal>
</template>

<script>
    import Layout from '@/Shared/Layout'
    import { useForm } from '@inertiajs/inertia-vue3'
    import SectionHeading from '@/Shared/SectionHeading'
    import Modal from '@/Shared/Modal'
    import Button from '@/Shared/Button'
    import { ref } from 'vue'
    import InviteComponent from '@/Pages/Invites/Create'
    import EditComponent from '@/Pages/Invites/Edit'
    import axios from 'axios'

    export default {
        setup (props) {
            let accountName = props.account.name
            const open = route().params.invite ? ref(true) : ref(false)
            const component = InviteComponent
            const expirationOptions = ref(null)
            const disableRoleSelect = ref(false)

            const form = useForm({
                _method: 'patch',
                name: accountName,
                domain: null,
                clientId: null,
                clientSecret: null,
            })
            return { form, open, component, expirationOptions, disableRoleSelect }
        },
        components: { SectionHeading, Button, Modal, InviteComponent },
        props: {
            account: Object,
            connection: [Object, Boolean],
            users: Object,
            sharedInvite: Object,
            invites: Object,
        },
        layout: Layout,
        methods: {
            deleteInvitation(invite) {
                this.$inertia.delete(route('invites.destroy', invite))
            },
            resetModal() {
                this.component = InviteComponent
            },
            deleteQueryParam() {
                if (route().params.invite) {
                    this.$inertia.visit(route('users.index'))
                }
            },
            async reloadSharedInvite() {
                this.$inertia.reload({only: ['sharedInvite']})
            },
            updateRole(user) {
                this.$inertia.patch(route('users.update.role', {user: user, role: user.selectedRole}), null, {
                    onStart: () => {
                        this.disableRoleSelect = true
                    },
                    onSuccess: () => {
                        this.disableRoleSelect = false
                    }
                })
            },
            deleteUser(user) {
                if (window.confirm('Do you want to delete ' + user.name + ' permanently? This can‘t be undone.')) {
                    this.$inertia.delete(route('users.destroy', user))
                }
            },
            switchComponents(token) {
                axios.get(route('invites.edit', token))
                    .then(res => {
                        this.expirationOptions = res.data.expirationOptions
                        if (!this.sharedInvite) {
                            this.reloadSharedInvite().then(r => {
                                this.component = EditComponent
                            })
                        } else {
                            this.component = EditComponent
                        }

                    })
                // this.component = 'EditComponent'
            }
        },
    }
</script>
